import { environment } from 'src/environments/environment';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { map, filter, take, timeout, catchError } from 'rxjs/operators';
import { LoginRequest } from '../dtos/LoginRequest';
import { TokenResponse } from '../dtos/TokenResponse';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private apiUrl = `${environment.apiUrl}/auth`;
  private tokenKey = 'authToken';
  private tenantKey = 'tenantId';

  private currentUserSubject = new BehaviorSubject<any>(this.getUserFromStorage());
  public currentUser$ = this.currentUserSubject.asObservable();

  // tenant observable
  private currentTenantSubject = new BehaviorSubject<string | null>(this.getTenantFromStorage());
  public currentTenant$ = this.currentTenantSubject.asObservable();

  constructor(private http: HttpClient) {
    this.checkToken();
  }

  /**
   * Faz login. Se tenantId for informado, armazena-o ANTES de enviar a requisição
   */
  login(credentials: LoginRequest, tenantId?: string): Observable<TokenResponse> {
    if (tenantId) {
      this.setTenant(tenantId);
    }

    return this.http.post<TokenResponse>(`${this.apiUrl}/login`, credentials).pipe(
      tap((response: TokenResponse) => {
        if (response && response.token) {
          this.setToken(response.token);
          const user = this.decodeToken(response.token);

          // Se o token contiver claim de tenant, garante que está armazenado
          const found = this.extractEmpresaFromDecoded(user);
          if (found) {
            this.setTenant(found);
          }

          this.currentUserSubject.next(user);

          // se token não traz roles, tenta buscar /api/usuarios/me para obter roles e demais campos
          const roles = this.getRoles();
          if (!roles || roles.length === 0) {
            this.loadUserFromBackend();
          }
        }
      })
    );
  }

  logout(): void {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.tenantKey);
    this.currentUserSubject.next(null);
    this.currentTenantSubject.next(null);
  }

  getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  setToken(token: string): void {
    localStorage.setItem(this.tokenKey, token);
  }

  // Tenant helpers
  setTenant(tenantId: string): void {
    if (!tenantId) return;
    localStorage.setItem(this.tenantKey, tenantId);
    this.currentTenantSubject.next(tenantId);
  }

  getTenant(): string | null {
    return localStorage.getItem(this.tenantKey);
  }

  clearTenant(): void {
    localStorage.removeItem(this.tenantKey);
    this.currentTenantSubject.next(null);
  }

  private getTenantFromStorage(): string | null {
    return localStorage.getItem(this.tenantKey);
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) {
      return false;
    }

    if (this.isTokenExpired(token)) {
      // Remove token expirado automaticamente
      this.logout();
      return false;
    }

    return true;
  }

  isTokenExpired(token: string): boolean {
    try {
      const decoded = this.decodeToken(token);
      if (!decoded || !decoded.exp) {
        return true;
      }
      const currentTime = Math.floor(Date.now() / 1000);
      return decoded.exp < currentTime;
    } catch (error) {
      return true;
    }
  }

  decodeToken(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload);
    } catch (error) {
      return null;
    }
  }

  getCurrentUser(): any {
    return this.currentUserSubject.value;
  }

  private getUserFromStorage(): any {
    const token = this.getToken();
    if (token) {
      return this.decodeToken(token);
    }
    return null;
  }

  private checkToken(): void {
    const token = this.getToken();
    if (token && this.isTokenExpired(token)) {
      this.logout();
    } else if (token) {
      const user = this.decodeToken(token);
      this.currentUserSubject.next(user);
      // if token has tenant claim, ensure it's loaded
      const found = this.extractEmpresaFromDecoded(user);
      if (found) {
        this.setTenant(found);
      }

      // se token não traz roles, buscar dados completos do usuário
      const roles = this.getRoles();
      if (!roles || roles.length === 0) {
        this.loadUserFromBackend();
      }
    }
  }

  /**
   * Procura por múltiplas claims possíveis que representam o ID da empresa/tenant.
   * Retorna o valor da primeira claim encontrada entre as aliases.
   */
  private extractEmpresaFromDecoded(decoded: any): string | null {
    if (!decoded) return null;
    const aliases = ['empresaId', 'empresa', 'tenantId', 'tenant'];
    for (const k of aliases) {
      if (decoded.hasOwnProperty(k) && decoded[k]) {
        return String(decoded[k]);
      }
    }
    return null;
  }

  /**
   * Retira o ID da empresa (tenant) do token se presente.
   * Procura por vários nomes de claim e faz fallback para storage.
   */
  getEmpresaId(): string | null {
    try {
      const token = this.getToken();
      // se não há token, retorna armazenamento (compat)
      if (!token) return this.getTenant();
      const decoded = this.decodeToken(token);
      if (!decoded) return this.getTenant();

      const found = this.extractEmpresaFromDecoded(decoded);
      if (found) {
        // garante que está armazenado para compatibilidade futura
        if (this.getTenant() !== found) {
          this.setTenant(found);
        }
        return found;
      }

      // fallback para storage
      return this.getTenant();
    } catch (e) {
      return this.getTenant();
    }
  }

  // --> NOVOS MÉTODOS PARA TRATAR ROLES/PERMISSÕES <--

  /**
   * Extrai um array de roles de valores possivelmente diferentes no token/objeto.
   * Aceita: roles: string[], authorities: string[] | {authority:string}[], role: string
   */
  getRoles(): string[] {
    const u = this.currentUserSubject.value;
    if (!u) return [];

    // Se já for array de strings
    if (Array.isArray(u.roles) && u.roles.every((r: any) => typeof r === 'string')) {
      return u.roles as string[];
    }

    // authorities pode ser array de objetos {authority: 'ROLE_X'} ou array de strings
    if (Array.isArray((u as any).authorities)) {
      const arr = (u as any).authorities;
      return arr.map((a: any) => (typeof a === 'string' ? a : a.authority || a)).filter(Boolean).map((s: any) => String(s));
    }

    // comprometido: role ou authority únicos
    if ((u as any).role) {
      return [String((u as any).role)];
    }
    if ((u as any).authority) {
      return [String((u as any).authority)];
    }

    return [];
  }

  /**
   * Verifica se o usuário atual possui a role informada (case-sensitive).
   */
  hasRole(role: string): boolean {
    if (!role) return false;
    const roles = this.getRoles();
    if (!roles || roles.length === 0) return false;

    // Normaliza: remove prefixos como ROLE_ e espaços
    const normalized = role.toString();
    return roles.some((r) => {
      if (!r) return false;
      const s = String(r).toUpperCase();
      const target = normalized.toUpperCase();
      // compara com/sem prefixo ROLE_
      if (s === target) return true;
      if (s === `ROLE_${target}`) return true;
      if (`ROLE_${s}` === target) return true;
      return false;
    });
  }

  /**
   * Garante que os dados do usuário (especialmente roles) estejam carregados.
   * Retorna um Observable que emite o usuário atual assim que as roles estiverem presentes,
   * ou emite o usuário atual após timeout (fallback).
   */
  ensureUserLoaded(timeoutMs: number = 3000): Observable<any> {
    const roles = this.getRoles();
    if (roles && roles.length > 0) {
      return of(this.getCurrentUser());
    }

    // Espera por uma emissão que contenha roles
    return this.currentUser$.pipe(
      map(() => this.getRoles()),
      filter((r) => Array.isArray(r) && r.length > 0),
      take(1),
      timeout(timeoutMs),
      map(() => this.getCurrentUser()),
      catchError(() => of(this.getCurrentUser()))
    );
  }

  /**
   * Quando o token não traz roles/dados completos, usa apenas os dados do token
   */
  private loadUserFromBackend(): void {
    try {
      console.log('[AuthService] carregando dados do usuário do token');

      // Usa os dados do token como usuário atual
      const decoded = this.decodeToken(this.getToken() || '');
      if (decoded) {
        console.log('[AuthService] Dados do usuário carregados do token');
        this.currentUserSubject.next(decoded);
      }
    } catch (e) {
      console.warn('[AuthService] Erro ao tentar decodificar token:', e);
    }
  }

}
